"use client";

/**
 * <AdminBanner> — 사용자 결정 #3: TradingView 식 상단 배너 (admin 토글).
 *
 * 동작:
 *   1) /api/banner 호출 → null 이면 안 띄움
 *   2) 활성 시 사이트 최상단에 배너 (HTML 직접 렌더 — admin 입력)
 *   3) 클릭 시 redirectUrl 로 이동 (없으면 무반응)
 *   4) 우측 X 버튼 — 세션 단위 dismiss (sessionStorage)
 *
 * 보안: HTML 은 admin 만 입력 가능 (POST /api/admin/banner) — XSS 위험 admin 자체에서만.
 */
import { useEffect, useState } from "react";
import { X } from "lucide-react";

interface BannerData {
  html: string;
  redirectUrl: string | null;
  bgColor: string;
  textColor: string;
}

const DISMISS_KEY = "bs:admin-banner:dismissed";

export function AdminBanner() {
  const [banner, setBanner] = useState<BannerData | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (sessionStorage.getItem(DISMISS_KEY) === "1") {
      setDismissed(true);
      return;
    }
    fetch("/api/banner", { credentials: "include" })
      .then((r) => r.ok ? r.json() : null)
      .then((d) => { if (d?.banner) setBanner(d.banner); })
      .catch(() => { /* ignore */ });
  }, []);

  if (!banner || dismissed) return null;

  const handleClick = () => {
    if (banner.redirectUrl) window.open(banner.redirectUrl, "_blank", "noopener,noreferrer");
  };

  const close = (e: React.MouseEvent) => {
    e.stopPropagation();
    sessionStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
  };

  return (
    <div
      className="bs-admin-banner"
      role="region"
      aria-label="공지 배너"
      style={{ background: banner.bgColor, color: banner.textColor }}
      onClick={banner.redirectUrl ? handleClick : undefined}
      data-clickable={banner.redirectUrl ? "1" : undefined}
    >
      <div
        className="bs-admin-banner-content"
        // admin 만 입력 가능 — XSS 책임 admin 측에서
        dangerouslySetInnerHTML={{ __html: banner.html }}
      />
      <button
        type="button"
        className="bs-admin-banner-close"
        onClick={close}
        aria-label="배너 닫기"
      >
        <X size={14} />
      </button>
      <style jsx global>{`
        .bs-admin-banner {
          position: relative;
          width: 100%;
          padding: 10px 44px 10px 18px;
          font-size: 13px;
          line-height: 1.5;
          z-index: 100;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .bs-admin-banner[data-clickable="1"] {
          cursor: pointer;
          transition: filter 120ms;
        }
        .bs-admin-banner[data-clickable="1"]:hover { filter: brightness(1.08); }
        .bs-admin-banner-content {
          flex: 1;
          text-align: center;
          max-width: 1200px;
        }
        .bs-admin-banner-content a {
          color: inherit;
          text-decoration: underline;
          text-underline-offset: 2px;
        }
        .bs-admin-banner-close {
          position: absolute;
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
          width: 24px;
          height: 24px;
          background: rgba(255, 255, 255, 0.12);
          color: inherit;
          border: 0;
          border-radius: 4px;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          opacity: 0.85;
          transition: opacity 120ms, background 120ms;
        }
        .bs-admin-banner-close:hover {
          background: rgba(255, 255, 255, 0.22);
          opacity: 1;
        }
      `}</style>
    </div>
  );
}
